/**
 * Where you are in kikik.id, as one small JSON-safe value.
 *
 * Three screens, in the order a visitor meets them: the portal, one game's
 * page, and the game itself. The address bar mirrors that with a hash route,
 * so Back leaves a table and lands on the portal instead of on the previous
 * website - and a link to `#/play/solo` opens straight at the table.
 *
 * Hash routing is opt-in (`main.tsx` turns it on) so that tests and the
 * production smoke run can mount a screen directly without a shared URL.
 */

import { useCallback, useEffect, useState } from "react";
import type { AppMode } from "../components";

export type View =
  | { name: "home" }
  | { name: "game"; slug: string }
  | { name: "play"; mode: AppMode };

const MODES: readonly AppMode[] = ["belajar", "solo", "online"];

export const HOME: View = { name: "home" };

export function viewToHash(view: View): string {
  switch (view.name) {
    case "game":
      return `#/game/${view.slug}`;
    case "play":
      return `#/play/${view.mode}`;
    default:
      return "#/";
  }
}

export function hashToView(hash: string): View | null {
  if (!hash.startsWith("#/")) return null;
  const parts = hash.slice(2).split("/").filter(Boolean);
  if (parts.length === 0) return HOME;
  const [head, tail] = parts;
  if (head === "game" && tail) return { name: "game", slug: tail };
  if (head === "play" && MODES.includes(tail as AppMode)) {
    return { name: "play", mode: tail as AppMode };
  }
  return null;
}

export function sameView(a: View, b: View): boolean {
  return viewToHash(a) === viewToHash(b);
}

/**
 * The current screen, kept in sync with `location.hash` when routing is on.
 *
 * With routing off this is ordinary React state, which is what the tests and
 * the bundled smoke check use.
 */
export function useView(initial: View, routing: boolean): [View, (next: View) => void] {
  const [view, setView] = useState<View>(() => {
    if (!routing) return initial;
    return hashToView(globalThis.location?.hash ?? "") ?? initial;
  });

  useEffect(() => {
    if (!routing) return;
    const onPop = (): void => {
      setView(hashToView(globalThis.location?.hash ?? "") ?? HOME);
    };
    globalThis.addEventListener?.("hashchange", onPop);
    return () => globalThis.removeEventListener?.("hashchange", onPop);
  }, [routing]);

  const go = useCallback(
    (next: View) => {
      setView(next);
      if (!routing) return;
      const hash = viewToHash(next);
      if (globalThis.location && globalThis.location.hash !== hash) {
        globalThis.location.hash = hash;
      }
    },
    [routing],
  );

  return [view, go];
}
